import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Send, Check, ShieldCheck, Mail, AlertCircle, Briefcase, ExternalLink } from "lucide-react";
import { ContactMessage } from "../types";
import { useLanguage } from "./LanguageContext";
import { trackEvent } from "./AnalyticsTracker";
import { saveLeadToFirebase } from "../lib/firebaseService";

export default function ContactForm() {
  const { t, language } = useLanguage();
  const [form, setForm] = useState<ContactMessage>({
    name: "",
    email: "",
    company: "",
    message: "",
  });

  const clientNoticeDict: Record<string, { label: string; action: string; emailNow: string; emailDirect: string }> = {
    en: { 
      label: "Are you a prospective corporate partner?", 
      action: "Send RFP or project inquiries directly to work@teralumax.com",
      emailNow: "Email Now",
      emailDirect: "Email Us Directly"
    },
    es: { 
      label: "¿Es un socio corporativo potencial?", 
      action: "Envíe su RFP o consultas directamente a work@teralumax.com",
      emailNow: "Enviar email",
      emailDirect: "Escríbanos directamente"
    },
    fr: { 
      label: "Partenaire professionnel potentiel ?", 
      action: "Envoyez vos appels d'offres ou demandes directement à work@teralumax.com",
      emailNow: "Écrire maintenant",
      emailDirect: "Écrivez-nous directement"
    },
    de: { 
      label: "Sind Sie ein potenzieller Firmenpartner?", 
      action: "Senden Sie Projektanfragen oder Ausschreibungen direkt an work@teralumax.com",
      emailNow: "Jetzt mailen",
      emailDirect: "Direkt per E-Mail"
    },
    hi: { 
      label: "क्या आप एक संभावित कॉर्पोरेट भागीदार हैं?", 
      action: "सीधे work@teralumax.com पर पूछताछ या प्रोजेक्ट विवरण भेजें",
      emailNow: "अभी ईमेल करें",
      emailDirect: "हमें सीधे ईमेल करें"
    },
    zh: { 
      label: "您是潜在的企业合作伙伴吗？", 
      action: "直接发送您的项目意向或咨询至 work@teralumax.com",
      emailNow: "现在发邮件",
      emailDirect: "直接发邮件给我们"
    },
    ja: { 
      label: "新規の法人パートナー様ですか？", 
      action: "プロジェクト・RFPのご相談は直接 work@teralumax.com までお寄せください",
      emailNow: "今すぐメール",
      emailDirect: "直接メールを送る"
    },
    ar: { 
      label: "هل أنت شريك تجاري محتمل؟", 
      action: "أرسل طلب مشروعك या استفسارك مباشرة إلى work@teralumax.com",
      emailNow: "راسلنا الآن",
      emailDirect: "بريد مباشر"
    },
  };

  const currentNotice = clientNoticeDict[language as string] || clientNoticeDict["en"];

  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFocus = (field: string) => setFocusedField(field);
  const handleBlur = () => setFocusedField(null);

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Simple validation rules
    if (!form.name.trim()) {
      setError(t("errName", "contact"));
      return;
    }
    if (!form.email.trim() || !validateEmail(form.email)) {
      setError(t("errEmail", "contact"));
      return;
    }
    if (!form.message.trim() || form.message.length < 10) {
      setError(t("errMessage", "contact"));
      return;
    }

    setLoading(true);

    // Save inquiry to localStorage to integrate seamlessly with the hidden Admin Dashboard
    try {
      const savedLeads = localStorage.getItem("teralumax_leads");
      const currentLeads = savedLeads ? JSON.parse(savedLeads) : [];
      const updatedLeads = [
        {
          name: form.name,
          email: form.email,
          company: form.company || "",
          message: form.message
        },
        ...currentLeads
      ];
      localStorage.setItem("teralumax_leads", JSON.stringify(updatedLeads));
      
      // Save lead to Firebase Firestore
      saveLeadToFirebase({
        name: form.name,
        email: form.email,
        company: form.company || "",
        message: form.message
      }).catch(err => console.error("Firebase lead save error:", err));

      // Track lead submission event in analytics telemetry
      trackEvent("form_success", `Secure contact lead submission received from ${form.name} (${form.company || "No Company"})`);

      // Dispatch storage event manually to notify open tabs immediately
      window.dispatchEvent(new Event("storage"));
    } catch (e) {
      console.error("Local storage sync error", e);
    }

    // Dynamic latency loading simulation for premium feel
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      setForm({ name: "", email: "", company: "", message: "" });
    }, 1800);
  };

  return (
    <section id="contact" className="relative py-28 md:py-36 bg-white overflow-hidden">
      {/* Absolute layered soft backlighting */}
      <div className="absolute top-[40%] left-[10%] w-96 h-96 ambient-light-indigo rounded-full blur-3xl opacity-40 pointer-events-none" />
      <div className="absolute bottom-[20%] right-[15%] w-80 h-80 ambient-light-purple rounded-full blur-3xl opacity-30 pointer-events-none" style={{ animationDelay: "2s" }} />

      <div className="max-w-4xl mx-auto px-6 relative z-10">
        
        {/* Contact Headings */}
        <div className="text-center mb-16 flex flex-col items-center gap-3">
          <span className="text-[10px] font-mono tracking-widest text-[#4F46E5] uppercase flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-pulse" /> {t("tag", "contact")}
          </span>
          <h3 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-brand-charcoal">
            {t("title", "contact")}
          </h3>
          <p className="text-gray-500 font-light text-sm max-w-md mx-auto leading-relaxed">
            {t("desc", "contact")}
          </p>
        </div>

        {/* Contact Container Grid */}
        <div className="max-w-2xl mx-auto">
          {/* Potential Clients Direct Email Notice */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="mb-8 p-5 rounded-2xl border border-indigo-100/70 bg-gradient-to-r from-indigo-50/40 via-indigo-50/10 to-transparent flex flex-col sm:flex-row items-center sm:items-start justify-between gap-4 backdrop-blur-md relative overflow-hidden group select-none text-left"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-200/10 rounded-full blur-2xl pointer-events-none group-hover:bg-indigo-200/20 transition-all duration-700" />
            <div className="flex items-start gap-4">
              <div className="p-3 bg-indigo-50 border border-indigo-100/50 rounded-xl text-indigo-600 shrink-0 shadow-xs group-hover:scale-105 transition-transform duration-300">
                <Briefcase size={18} />
              </div>
              <div className="flex flex-col gap-1 text-center sm:text-left">
                <span className="text-[11px] font-mono tracking-widest text-indigo-600 uppercase font-semibold">
                  {currentNotice.label}
                </span>
                <p className="text-xs text-gray-500 font-light leading-relaxed">
                  {currentNotice.action.split("work@teralumax.com")[0]}
                  <span className="font-mono text-indigo-700 font-semibold bg-indigo-50/50 border border-indigo-100/30 px-1.5 py-0.5 rounded-md hover:bg-indigo-100/50 transition-colors">
                    work@teralumax.com
                  </span>
                  {currentNotice.action.split("work@teralumax.com")[1]}
                </p>
              </div>
            </div>
            <a
              href="mailto:work@teralumax.com"
              aria-label="Direct email to Teralumax"
              className="mt-2 sm:mt-0 px-5 py-3 bg-white border border-gray-200 hover:border-indigo-600 text-gray-700 hover:text-indigo-600 text-xs font-mono uppercase tracking-wider rounded-xl transition-all flex items-center gap-2 shrink-0 shadow-xs hover:shadow-indigo-500/5 cursor-pointer active:scale-95 select-none min-h-[44px]"
            >
              <span>{currentNotice.emailNow}</span>
              <ExternalLink size={11} className="text-gray-400 group-hover:text-indigo-500 transition-colors" />
            </a>
          </motion.div>

          <div className="relative rounded-2xl border border-gray-200/80 bg-white/50 p-8 md:p-10 shadow-2xl backdrop-blur-md overflow-hidden">
            <div className="absolute inset-0 noise-overlay pointer-events-none" />

            <AnimatePresence mode="wait">
              {!submitted ? (
                <motion.form
                  key="contact-form"
                  onSubmit={handleSubmit}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col gap-6 text-left"
                >
                  {/* Error Prompt alerts */}
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="p-4 bg-red-50 text-red-700 text-xs font-mono rounded-xl border border-red-100 flex items-center gap-2.5"
                      >
                        <AlertCircle size={15} /> {error}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Input Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Name input */}
                    <div className="relative flex flex-col gap-1.5 text-left">
                      <label htmlFor="name-input" className="text-[10px] font-mono uppercase tracking-wider text-gray-400">
                        {t("labelName", "contact")} <span className="text-indigo-500">*</span>
                      </label>
                      <input
                        id="name-input"
                        type="text"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        onFocus={() => handleFocus("name")}
                        onBlur={handleBlur}
                        placeholder={t("placeholderName", "contact")}
                        className={`px-4.5 py-3.5 text-sm bg-white/60 border rounded-xl outline-hidden font-sans transition-all placeholder:text-gray-300 ${
                          focusedField === "name"
                            ? "border-indigo-500 ring-4 ring-indigo-500/5 shadow-xs"
                            : "border-gray-200"
                        }`}
                      />
                    </div>

                    {/* Email input */}
                    <div className="relative flex flex-col gap-1.5 text-left">
                      <label htmlFor="email-input" className="text-[10px] font-mono uppercase tracking-wider text-gray-400">
                        {t("labelEmail", "contact")} <span className="text-indigo-500">*</span>
                      </label>
                      <input
                        id="email-input"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        onFocus={() => handleFocus("email")}
                        onBlur={handleBlur}
                        placeholder={t("placeholderEmail", "contact")}
                        className={`px-4.5 py-3.5 text-sm bg-white/60 border rounded-xl outline-hidden font-sans transition-all placeholder:text-gray-300 ${
                          focusedField === "email"
                            ? "border-indigo-500 ring-4 ring-indigo-500/5 shadow-xs"
                            : "border-gray-200"
                        }`}
                      />
                    </div>
                  </div>

                  {/* Company input */}
                  <div className="relative flex flex-col gap-1.5 text-left">
                    <label htmlFor="company-input" className="text-[10px] font-mono uppercase tracking-wider text-gray-400">
                      {t("labelCompany", "contact")} <span className="text-gray-300">(Optional)</span>
                    </label>
                    <input
                      id="company-input"
                      type="text"
                      value={form.company}
                      onChange={(e) => setForm({ ...form, company: e.target.value })}
                      onFocus={() => handleFocus("company")}
                      onBlur={handleBlur}
                      placeholder={t("placeholderCompany", "contact")}
                      className={`px-4.5 py-3.5 text-sm bg-white/60 border rounded-xl outline-hidden font-sans transition-all placeholder:text-gray-300 ${
                        focusedField === "company"
                          ? "border-indigo-500 ring-4 ring-indigo-500/5 shadow-xs"
                          : "border-gray-200"
                      }`}
                    />
                  </div>

                  {/* Message input */}
                  <div className="relative flex flex-col gap-1.5 text-left">
                    <label htmlFor="message-input" className="text-[10px] font-mono uppercase tracking-wider text-gray-400">
                      {t("labelMessage", "contact")} <span className="text-indigo-500">*</span>
                    </label>
                    <textarea
                      id="message-input"
                      rows={5}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      onFocus={() => handleFocus("message")}
                      onBlur={handleBlur}
                      placeholder={t("placeholderMessage", "contact")}
                      className={`px-4.5 py-3.5 text-sm bg-white/60 border rounded-xl outline-hidden font-sans transition-all placeholder:text-gray-305 resize-none leading-relaxed ${
                        focusedField === "message"
                          ? "border-indigo-500 ring-4 ring-indigo-500/5 shadow-xs"
                          : "border-gray-200"
                      }`}
                    />
                  </div>

                  {/* Submit Action Button */}
                  <div className="mt-4 flex flex-col md:flex-row items-center justify-between gap-5 border-t border-gray-100 pt-6">
                    <p className="text-[11px] font-mono text-gray-400 flex items-center gap-1.5 leading-normal text-left">
                      <ShieldCheck size={13} className="text-emerald-500 shrink-0" />
                      {t("privacyText", "contact")}
                    </p>
                    <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
                      <a
                        href="mailto:work@teralumax.com?subject=Potential%20Client%20Inquiry%20-%20Teralumax&body=Hello%20Teralumax%20Team%2C%0D%0A%0D%0AI%20am%20interested%20in%20discussing%20a%20potential%20corporate%20partnership.%0D%0A%0D%0ACompany%20Name%3A%20%0D%0AProject%20Scope%3A%20%0D%0A%0D%0ABest%20regards%2C%0D%0A"
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-xs font-mono tracking-widest border border-indigo-200 hover:border-indigo-600 text-indigo-700 hover:bg-indigo-50/50 px-6 py-4 rounded-full transition-all focus:outline-hidden cursor-pointer min-h-[44px]"
                        aria-label="Email Us Directly to work@teralumax.com"
                      >
                        <span>{currentNotice.emailDirect}</span>
                        <Mail size={12} className="shrink-0 text-indigo-500" />
                      </a>
                      <button
                        type="submit"
                        disabled={loading}
                        aria-label="Send message click"
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 text-xs font-mono tracking-widest bg-brand-charcoal text-white hover:bg-indigo-600 hover:shadow-lg hover:shadow-indigo-500/10 px-8 py-4 rounded-full transition-all focus:outline-hidden relative cursor-pointer"
                      >
                        {loading ? (
                          <>
                            <div className="w-3.5 h-3.5 border-2 border-indigo-200 border-t-white rounded-full animate-spin" />
                            <span>{t("btnLoading", "contact")}</span>
                          </>
                        ) : (
                          <>
                            <span>{t("btnSubmit", "contact")}</span>
                            <Send size={12} />
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </motion.form>
              ) : (
                /* Success Animated panel */
                <motion.div
                  key="contact-success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="py-12 text-center flex flex-col items-center gap-5"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 150, delay: 0.1 }}
                    className="w-16 h-16 bg-emerald-50 border border-emerald-150 rounded-full flex items-center justify-center text-emerald-500 mb-2 shadow-xs"
                  >
                    <Check size={28} />
                  </motion.div>
                  <h4 className="font-display font-medium text-2xl text-black">
                    {t("successTitle", "contact")}
                  </h4>
                  <p className="text-gray-500 font-light text-sm max-w-sm mx-auto leading-relaxed">
                    {t("successText", "contact")}
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-4 px-6.5 py-3 text-xs font-mono tracking-widest rounded-full bg-gray-50 border border-gray-200 text-gray-500 hover:text-black hover:bg-white hover:border-gray-999 transition-all focus:outline-hidden cursor-pointer"
                  >
                    {t("anotherCta", "contact")}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

      </div>
    </section>
  );
}
